/**
 * The order lifecycle.
 *
 * This module is the single definition of what an order's status can be and how
 * it may change. The Worker enforces it; the interface reads it to decide which
 * actions to offer. Neither invents transitions of its own.
 *
 * A laundry order is not a shopping cart — it is a physical custody chain. The
 * shop has someone's clothes, and the status says where in that chain they are.
 * That is why the transitions are a narrow graph rather than a free-form field:
 * "delivered" must mean the garments actually left with the customer, and an
 * order cannot quietly go back to being in the wash afterwards.
 */

export const ORDER_STATUSES = [
  "RECEIVED",
  "INSPECTION",
  "PROCESSING",
  "READY",
  "DELIVERED",
  "CANCELLED",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export interface OrderStatusDefinition {
  readonly status: OrderStatus;
  readonly labelAr: string;
  readonly labelEn: string;
  /** Maps to the Badge tone in the interface. */
  readonly tone: "neutral" | "info" | "warning" | "success" | "danger" | "accent";
  readonly descriptionAr: string;
  readonly descriptionEn: string;
}

export const ORDER_STATUS_DEFINITIONS: Readonly<Record<OrderStatus, OrderStatusDefinition>> = {
  RECEIVED: {
    status: "RECEIVED",
    labelAr: "مستلَم",
    labelEn: "Received",
    tone: "neutral",
    descriptionAr: "تم استلام القطع من العميل.",
    descriptionEn: "The items have been taken in from the customer.",
  },
  INSPECTION: {
    status: "INSPECTION",
    labelAr: "قيد الفحص",
    labelEn: "Inspection",
    tone: "info",
    descriptionAr: "يجري فحص القطع وتسجيل البقع والتلف قبل التشغيل.",
    descriptionEn: "Items are being checked for stains and damage before processing.",
  },
  PROCESSING: {
    status: "PROCESSING",
    labelAr: "قيد التشغيل",
    labelEn: "Processing",
    tone: "warning",
    descriptionAr: "القطع في الغسيل أو الكي أو التنظيف الجاف.",
    descriptionEn: "Items are being washed, ironed or dry cleaned.",
  },
  READY: {
    status: "READY",
    labelAr: "جاهز للاستلام",
    labelEn: "Ready for pickup",
    tone: "accent",
    descriptionAr: "اكتمل العمل والطلب بانتظار العميل.",
    descriptionEn: "Work is finished and the order is waiting for the customer.",
  },
  DELIVERED: {
    status: "DELIVERED",
    labelAr: "تم التسليم",
    labelEn: "Delivered",
    tone: "success",
    descriptionAr: "استلم العميل القطع. حالة نهائية.",
    descriptionEn: "The customer has taken the items. Final state.",
  },
  CANCELLED: {
    status: "CANCELLED",
    labelAr: "ملغى",
    labelEn: "Cancelled",
    tone: "danger",
    descriptionAr: "أُلغي الطلب ولا يُحتسب ضمن المستحقات. حالة نهائية.",
    descriptionEn: "The order was cancelled and is excluded from balances. Final state.",
  },
};

/**
 * The allowed transitions.
 *
 * Forward-only along the custody chain, with cancellation available while the
 * shop still has the items.
 *
 *   RECEIVED   → INSPECTION, PROCESSING, CANCELLED
 *   INSPECTION → PROCESSING, CANCELLED
 *   PROCESSING → READY, CANCELLED
 *   READY      → DELIVERED, CANCELLED
 *   DELIVERED  → (terminal)
 *   CANCELLED  → (terminal)
 *
 * Two deliberate choices:
 *
 * RECEIVED may skip straight to PROCESSING. Inspection is a real step for
 * dry cleaning and a ceremony for a bag of shirts; forcing it would train staff
 * to click through a meaningless screen, which is how status fields stop
 * reflecting reality.
 *
 * DELIVERED is terminal and cannot be cancelled. Once the garments have left
 * with the customer the shop can no longer undo the transaction by editing a
 * field — that is a refund, which is a financial event, not a status change.
 */
const ALLOWED_TRANSITIONS: Readonly<Record<OrderStatus, readonly OrderStatus[]>> = {
  RECEIVED: ["INSPECTION", "PROCESSING", "CANCELLED"],
  INSPECTION: ["PROCESSING", "CANCELLED"],
  PROCESSING: ["READY", "CANCELLED"],
  READY: ["DELIVERED", "CANCELLED"],
  DELIVERED: [],
  CANCELLED: [],
};

export const INITIAL_ORDER_STATUS: OrderStatus = "RECEIVED";

/** Statuses an order can never leave. */
export const TERMINAL_ORDER_STATUSES: readonly OrderStatus[] = ["DELIVERED", "CANCELLED"];

export function isOrderStatus(value: unknown): value is OrderStatus {
  return typeof value === "string" && (ORDER_STATUSES as readonly string[]).includes(value);
}

export function isTerminalStatus(status: OrderStatus): boolean {
  return TERMINAL_ORDER_STATUSES.includes(status);
}

/** The statuses an order may move to from where it is now. */
export function allowedTransitionsFrom(status: OrderStatus): readonly OrderStatus[] {
  return ALLOWED_TRANSITIONS[status];
}

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export interface TransitionRefusal {
  readonly code: "same_status" | "terminal_status" | "transition_not_allowed";
  readonly messageEn: string;
  readonly messageAr: string;
}

/**
 * Explains why a transition is refused, or returns null when it is allowed.
 *
 * Returns a reason rather than a boolean so the API can tell the user which of
 * three quite different things happened: nothing to do, the order is finished,
 * or that particular jump is not part of the workflow.
 */
export function refuseTransition(from: OrderStatus, to: OrderStatus): TransitionRefusal | null {
  if (from === to) {
    return {
      code: "same_status",
      messageEn: `The order is already ${ORDER_STATUS_DEFINITIONS[to].labelEn.toLowerCase()}.`,
      messageAr: `الطلب بالفعل في حالة «${ORDER_STATUS_DEFINITIONS[to].labelAr}».`,
    };
  }

  if (isTerminalStatus(from)) {
    return {
      code: "terminal_status",
      messageEn: `A ${ORDER_STATUS_DEFINITIONS[from].labelEn.toLowerCase()} order cannot change status.`,
      messageAr: `لا يمكن تغيير حالة طلب «${ORDER_STATUS_DEFINITIONS[from].labelAr}».`,
    };
  }

  if (!canTransition(from, to)) {
    return {
      code: "transition_not_allowed",
      messageEn: `An order cannot move from ${ORDER_STATUS_DEFINITIONS[from].labelEn.toLowerCase()} to ${ORDER_STATUS_DEFINITIONS[to].labelEn.toLowerCase()}.`,
      messageAr: `لا يمكن نقل الطلب من «${ORDER_STATUS_DEFINITIONS[from].labelAr}» إلى «${ORDER_STATUS_DEFINITIONS[to].labelAr}».`,
    };
  }

  return null;
}

/**
 * Statuses whose orders still count toward a customer's outstanding balance.
 *
 * Cancelled orders are excluded: the shop is not owed money for work it did not
 * do. Delivered orders are INCLUDED, because delivering an order does not mean
 * it was paid for — an unpaid delivered order is exactly the debt a laundry
 * needs to see.
 */
export const BALANCE_BEARING_STATUSES: readonly OrderStatus[] = [
  "RECEIVED",
  "INSPECTION",
  "PROCESSING",
  "READY",
  "DELIVERED",
];

/** Statuses where the items are physically still in the shop. */
export const IN_SHOP_STATUSES: readonly OrderStatus[] = [
  "RECEIVED",
  "INSPECTION",
  "PROCESSING",
  "READY",
];

/* ── Pricing ─────────────────────────────────────────────────────────────── */

export type PricingMode = "per_item" | "per_kg" | "flat";

export const PRICING_MODES: readonly PricingMode[] = ["per_item", "per_kg", "flat"];

export interface PricingModeDefinition {
  readonly mode: PricingMode;
  readonly labelAr: string;
  readonly labelEn: string;
  /** Unit shown beside the quantity field. */
  readonly unitAr: string;
  readonly unitEn: string;
  /** Whether a fractional quantity makes sense. */
  readonly allowsFractionalQuantity: boolean;
}

export const PRICING_MODE_DEFINITIONS: Readonly<Record<PricingMode, PricingModeDefinition>> = {
  per_item: {
    mode: "per_item",
    labelAr: "بالقطعة",
    labelEn: "Per item",
    unitAr: "قطعة",
    unitEn: "pcs",
    allowsFractionalQuantity: false,
  },
  per_kg: {
    mode: "per_kg",
    labelAr: "بالكيلو",
    labelEn: "Per kilogram",
    unitAr: "كجم",
    unitEn: "kg",
    allowsFractionalQuantity: true,
  },
  flat: {
    mode: "flat",
    labelAr: "سعر ثابت",
    labelEn: "Flat rate",
    unitAr: "—",
    unitEn: "—",
    allowsFractionalQuantity: false,
  },
};

export function isPricingMode(value: unknown): value is PricingMode {
  return typeof value === "string" && (PRICING_MODES as readonly string[]).includes(value);
}
